import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { UserAgentSkill } from "@/types/workflowEngine";
import {
  SkillBuildForm,
  defaultSkillFormValues,
  skillToFormValues,
  validateSkillForm,
  type SkillFormValues,
} from "@/components/agents/SkillBuildForm";

export interface SkillBuilderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill?: UserAgentSkill | null;
  initialValues?: Partial<SkillFormValues>;
  onSave: (values: SkillFormValues) => Promise<void>;
  isSaving?: boolean;
}

export function SkillBuilderModal({
  open,
  onOpenChange,
  skill,
  initialValues,
  onSave,
  isSaving,
}: SkillBuilderModalProps) {
  const [form, setForm] = useState<SkillFormValues & { outputSchemaDraftInvalid?: boolean }>(
    defaultSkillFormValues
  );

  useEffect(() => {
    if (!open) return;
    if (skill) {
      setForm(skillToFormValues(skill));
    } else {
      setForm({ ...defaultSkillFormValues, ...initialValues });
    }
  }, [open, skill, initialValues]);

  const error = validateSkillForm(form);

  const handleSave = async () => {
    if (error) return;
    await onSave(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{skill ? "Edit Skill" : "Create Skill"}</DialogTitle>
        </DialogHeader>
        <SkillBuildForm
          value={form}
          onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
        />
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!!error || isSaving} data-testid="button-save-skill">
            {isSaving ? "Saving..." : skill ? "Save Changes" : "Create Skill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
